'use client';

import React from 'react';

interface VirtualKeyboardProps {
  visible: boolean;
  onKeyPress: (key: string) => void;
  onBackspace: () => void;
  onEnter: () => void;
  onShift: () => void;
  onLanguageToggle: () => void;
  isShifted: boolean;
  currentLanguage: 'ko' | 'en';
}

export default function VirtualKeyboard({
  visible,
  onKeyPress,
  onBackspace,
  onEnter,
  onShift,
  onLanguageToggle,
  isShifted,
  currentLanguage,
}: VirtualKeyboardProps) {
  if (!visible) return null;

  const preventBlur = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  // 한글 레이아웃 (두벌식 쿼티)
  const koreanLayout = {
    normal: [
      ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'],
      ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'],
      ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ'],
    ],
    shifted: [
      ['ㅃ', 'ㅉ', 'ㄸ', 'ㄲ', 'ㅆ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅒ', 'ㅖ'],
      ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ'],
      ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ'],
    ],
  };

  // 영어 레이아웃
  const englishLayout = {
    normal: [
      ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
      ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
      ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
    ],
    shifted: [
      ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
      ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
      ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
    ],
  };

  // 숫자 및 특수문자 행
  const numbersRow = {
    normal: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    shifted: ['!', '@', '#', '$', '%', '^', '&', '*', '(', ')'],
  };

  const layout =
    currentLanguage === 'ko'
      ? isShifted
        ? koreanLayout.shifted
        : koreanLayout.normal
      : isShifted
        ? englishLayout.shifted
        : englishLayout.normal;

  const numbers = isShifted ? numbersRow.shifted : numbersRow.normal;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 p-4 shadow-lg z-50">
      {/* 숫자 및 특수문자 행 */}
      <div className="flex gap-1 mb-2 justify-center">
        {numbers.map((key) => (
          <button
            key={key}
            onMouseDown={preventBlur}
            onClick={() => onKeyPress(key)}
            className="flex-1 max-w-[80px] h-12 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-lg"
          >
            {key}
          </button>
        ))}
        <button
          onMouseDown={preventBlur}
          onClick={onBackspace}
          className="flex-1 max-w-[120px] h-12 bg-red-600 hover:bg-red-500 text-white rounded font-medium"
        >
          ⌫ 지우기
        </button>
      </div>

      {/* 첫 번째 행 */}
      <div className="flex gap-1 mb-2 justify-center">
        {layout[0].map((key) => (
          <button
            key={key}
            onMouseDown={preventBlur}
            onClick={() => onKeyPress(key)}
            className="flex-1 max-w-[80px] h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-xl"
          >
            {key}
          </button>
        ))}
      </div>

      {/* 두 번째 행 */}
      <div className="flex gap-1 mb-2 justify-center">
        <div className="w-[40px]"></div> {/* 왼쪽 여백 */}
        {layout[1].map((key) => (
          <button
            key={key}
            onMouseDown={preventBlur}
            onClick={() => onKeyPress(key)}
            className="flex-1 max-w-[80px] h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-xl"
          >
            {key}
          </button>
        ))}
        <div className="w-[40px]"></div> {/* 오른쪽 여백 */}
      </div>

      {/* 세 번째 행 */}
      <div className="flex gap-1 mb-2 justify-center">
        <button
          onMouseDown={preventBlur}
          onClick={onShift}
          className={`w-[100px] h-14 rounded font-medium ${
            isShifted
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-gray-600 hover:bg-gray-500 text-white'
          }`}
        >
          ⇧ Shift
        </button>
        {layout[2].map((key) => (
          <button
            key={key}
            onMouseDown={preventBlur}
            onClick={() => onKeyPress(key)}
            className="flex-1 max-w-[80px] h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-xl"
          >
            {key}
          </button>
        ))}
        <button
          onMouseDown={preventBlur}
          onClick={onEnter}
          className="w-[100px] h-14 bg-green-600 hover:bg-green-500 text-white rounded font-medium"
        >
          Enter
        </button>
      </div>

      {/* 네 번째 행 (언어 전환, 스페이스바) */}
      <div className="flex gap-1 justify-center">
        <button
          onMouseDown={preventBlur}
          onClick={onLanguageToggle}
          className="w-[120px] h-14 bg-gray-700 hover:bg-gray-600 text-white rounded font-medium"
        >
          {currentLanguage === 'ko' ? '한/A' : 'A/한'}
        </button>
        <button
          onMouseDown={preventBlur}
          onClick={() => onKeyPress(' ')}
          className="flex-1 h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium"
        >
          Space
        </button>
        <button
          onMouseDown={preventBlur}
          onClick={() => onKeyPress('-')}
          className="w-[80px] h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-xl"
        >
          -
        </button>
        <button
          onMouseDown={preventBlur}
          onClick={() => onKeyPress('.')}
          className="w-[80px] h-14 bg-gray-600 hover:bg-gray-500 text-white rounded font-medium text-xl"
        >
          .
        </button>
      </div>
    </div>
  );
}
